/**
 * Backgammon Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum BackgammonErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface BackgammonError {
  type: BackgammonErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class BackgammonErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: BackgammonError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: BackgammonErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): BackgammonError {
    const error: BackgammonError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Backgammon', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: BackgammonErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case BackgammonErrorType.INVALID_MOVE:
      case BackgammonErrorType.NOT_YOUR_TURN:
      case BackgammonErrorType.GAME_ENDED:
      case BackgammonErrorType.GAME_LOGIC:
      case BackgammonErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case BackgammonErrorType.CONNECTION_ERROR:
      case BackgammonErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case BackgammonErrorType.CORRUPTED_DATA:
      case BackgammonErrorType.SAVE_ERROR:
      case BackgammonErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): BackgammonError {
    return this.handleError(
      BackgammonErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(from: number, to: number): BackgammonError {
    return this.handleError(
      BackgammonErrorType.INVALID_MOVE,
      `Invalid move from point ${from} to point ${to}.`,
      { from, to }
    );
  }

  handleNotYourTurn(): BackgammonError {
    return this.handleError(
      BackgammonErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): BackgammonError {
    return this.handleError(
      BackgammonErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): BackgammonError {
    return this.handleError(
      BackgammonErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): BackgammonError {
    return this.handleError(
      BackgammonErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): BackgammonError {
    return this.handleError(
      BackgammonErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: BackgammonErrorType): boolean {
    switch (type) {
      case BackgammonErrorType.INVALID_MOVE:
      case BackgammonErrorType.NOT_YOUR_TURN:
      case BackgammonErrorType.GAME_ENDED:
      case BackgammonErrorType.GAME_LOGIC:
      case BackgammonErrorType.CONNECTION_ERROR:
      case BackgammonErrorType.CORRUPTED_DATA:
      case BackgammonErrorType.AI_TIMEOUT:
      case BackgammonErrorType.SAVE_ERROR:
      case BackgammonErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: BackgammonError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: BackgammonError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Backgammon Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): BackgammonError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
